/** Someone else's split cache is never part of the personal sync stream. */
import { db, nowIso } from "./db";
import { requireUser, nextUpdatedAt } from "./repo";
import { queueSync } from "./sync";
import { readCachedEntitlement } from "./entitlement";
import { defaultProfile } from "./profile_defaults";
import { isSavingsCategory } from "@/domain/category_kind";
import { myShareHome, computeBalances, computeShares, SplitRowError, MissingHomeRateError } from "@/domain/splits";
import { cents, MAX_AMOUNT_CENTS, type RateNano } from "@/domain/money";
import { fromWireLinkedGroup, fromWireLinkedExpense, fromWireLinkedTransfer, type LinkedGroup, type LinkedExpense, type LinkedTransfer, type LocalExpense } from "./types";
import type { LinkedSplit } from "./backend";
export const LINKED_IMPORT_CATEGORY_KEY = "split:import:category";
export const LINKED_SHARE_RATE_REMEDY = "Enter the rate again in the shared group to check this share.";
export const LINKED_LIST_KEY = "split:linked";
export const linkedCursorKey = (id: string): string => `split:link:${id}:since`;
export const linkedRateKey = (id: string): string => `split:link:${id}:rate`;
export interface LinkedDetail { link: LinkedSplit; group: LinkedGroup; expenses: LinkedExpense[]; transfers: LinkedTransfer[]; balances: Map<string, number>; skipped: number }
export async function readLinkedList(): Promise<LinkedSplit[]> {
  const value = await db.meta.get(LINKED_LIST_KEY);
  return value ? JSON.parse(value.value) as LinkedSplit[] : [];
}
/** Cache eviction, not deletion of a user's ledger. Reset permits an owner undo to resume. */
export async function evictLinkedSplit(id: string): Promise<void> {
  await db.transaction("rw", db.linkedSplitRows, db.meta, async () => {
    await db.linkedSplitRows.where("linkId").equals(id).delete();
    await db.meta.put({ key: linkedCursorKey(id), value: "0" });
  });
}
export async function readLinkedDetail(id: string): Promise<LinkedDetail | null> {
  const link = (await readLinkedList()).find(link => link.link_id === id);
  if (!link || link.ended) return null;
  const rows = await db.linkedSplitRows.where("linkId").equals(id).toArray();
  const groupRow = rows.find(row => row.table === "split_groups" && !row.deleted);
  if (!groupRow) return null;
  const group = fromWireLinkedGroup(JSON.parse(groupRow.payload));
  const expenses = rows.filter(row => row.table === "split_expenses").map(row => ({ ...fromWireLinkedExpense(JSON.parse(row.payload)), ...(row.deleted ? { deletedAt: row.updatedAt ?? nowIso() } : {}) }));
  const transfers = rows.filter(row => row.table === "split_transfers" && !row.deleted).map(row => fromWireLinkedTransfer(JSON.parse(row.payload)));
  const result = computeBalances(group, expenses.filter(row => !row.deletedAt), transfers);
  return { link, group, expenses, transfers, balances: result.balances, skipped: result.skipped.length };
}
export function linkedShareAmount(detail: LinkedDetail, expense: LinkedExpense, currency: string, rate: RateNano | null) {
  const share = computeShares(expense.amountMinor, expense.mode, expense.entries, expense.currency).get(detail.link.member_id) ?? 0;
  return share === 0 ? cents(0) : myShareHome(expense, detail.link.member_id, currency, rate, detail.group.baseCurrency, detail.group.members.map(member => member.id));
}
export function linkedShareState(detail: LinkedDetail, source: LinkedExpense, imported: LocalExpense | undefined, currency: string): "new" | "imported" | "update" | "deleted" | "unreadable" {
  if (!imported || imported.deletedAt !== null) return "new";
  if (source.deletedAt !== null) return "deleted";
  try {
    const amount = linkedShareAmount(detail, source, imported.splitRef?.homeCurrency ?? currency, imported.splitRef?.homeRateNano ?? null);
    if (amount === 0) return "deleted";
    return source.updatedAt > (imported.splitRef?.splitUpdatedAt ?? "") || amount !== imported.amountCents ? "update" : "imported";
  } catch (cause) {
    if (!(cause instanceof SplitRowError || cause instanceof MissingHomeRateError)) throw cause;
    return "unreadable";
  }
}
export function assertLinkedImportAllowed(): void {
  const cache = readCachedEntitlement(requireUser());
  if (cache && (cache.entitlement.state === "none" || cache.entitlement.captureBlocked)) throw new Error("Choose or renew a plan to add shares to your ledger.");
}
export const linkedShareId = (linkId: string, expenseId: string): string => `linked-share:${encodeURIComponent(linkId)}:${encodeURIComponent(expenseId)}`;
export async function importLinkedShares(linkId: string, items: readonly string[], options: { categoryId: string; homeRateNano: RateNano | null; update?: boolean }): Promise<number> {
  assertLinkedImportAllowed();
  const owner = requireUser();
  const count = await db.transaction("rw", db.expenses, db.categories, db.profile, db.linkedSplitRows, db.meta, async () => {
    const detail = await readLinkedDetail(linkId);
    if (!detail) throw new Error("This group is no longer shared. Ask the owner for a new invite.");
    const category = await db.categories.get(options.categoryId);
    if (!category || category.userId !== owner || category.deletedAt || category.archivedAt || isSavingsCategory(category)) throw new Error("Choose a live personal spending category.");
    const currency = (await db.profile.get(owner))?.currency ?? defaultProfile(owner, nowIso()).currency;
    let written = 0;
    for (const sourceId of new Set(items)) {
      const source = detail.expenses.find(row => row.id === sourceId);
      if (!source || source.deletedAt) throw new Error("This expense was deleted. Review your imported share before deleting yours.");
      const matches = await db.expenses.where("splitRef.splitExpenseId").equals(sourceId).filter(row => row.userId === owner && row.splitRef?.linkId === linkId).toArray();
      const existing = matches.find(row => !row.deletedAt);
      if (existing && !options.update) continue;
      if (existing?.splitRef?.homeCurrency && existing.splitRef.homeCurrency !== currency) throw new Error(`Switch your profile currency back to ${existing.splitRef.homeCurrency} before updating this share.`);
      const amount = linkedShareAmount(detail, source, currency, options.homeRateNano);
      if (amount > MAX_AMOUNT_CENTS) throw new SplitRowError("Your share exceeds the ledger limit. Ask the owner to reduce the split.");
      if (amount === 0) {
        if (existing && options.update) throw new Error("Your share is now zero. Review Delete yours? in the shared group to delete it explicitly.");
        continue;
      }
      const id = existing?.id ?? linkedShareId(linkId, sourceId);
      const prior = matches.find(row => row.id === id);
      const at = nextUpdatedAt(existing?.updatedAt ?? prior?.updatedAt);
      const row: LocalExpense = { id, userId: owner, categoryId: existing?.categoryId ?? options.categoryId, amountCents: amount,
        note: existing ? existing.note : source.description, occurredAt: source.occurredAt, recurringRuleId: null, sourceId: null,
        splitRef: { linkId, groupId: detail.group.id, splitExpenseId: source.id, splitUpdatedAt: source.updatedAt, homeCurrency: currency, homeRateNano: options.homeRateNano },
        createdAt: existing?.createdAt ?? prior?.createdAt ?? at, updatedAt: at, deletedAt: null, _dirty: 1, _syncedAt: null };
      await db.expenses.put(row); written++;
    }
    if (written) {
      await db.meta.put({ key: linkedRateKey(linkId), value: JSON.stringify(options.homeRateNano) });
      await db.meta.put({ key: LINKED_IMPORT_CATEGORY_KEY, value: options.categoryId });
    }
    return written;
  });
  if (count) queueSync();
  return count;
}
export async function deleteImportedShare(linkId: string, sourceId: string): Promise<void> {
  assertLinkedImportAllowed();
  await db.transaction("rw", db.expenses, db.linkedSplitRows, db.meta, async () => {
    const source = await db.linkedSplitRows.get([linkId, "split_expenses", sourceId]);
    const rows = await db.expenses.where("splitRef.splitExpenseId").equals(sourceId).filter(row => row.userId === requireUser() && row.splitRef?.linkId === linkId && !row.deletedAt).toArray();
    if (source && !source.deleted) {
      const detail = await readLinkedDetail(linkId);
      const expense = detail?.expenses.find(row => row.id === sourceId);
      if (detail && expense && rows.some(row => linkedShareState(detail, expense, row, row.splitRef?.homeCurrency ?? "") !== "deleted")) throw new Error("The source is still shared. Use Update from split instead.");
    }
    for (const row of rows) { const at = nextUpdatedAt(row.updatedAt); await db.expenses.put({ ...row, deletedAt: at, updatedAt: at, _dirty: 1 }); }
  });
  queueSync();
}
